/**
 * useOfflineQueue Hook
 *
 * Purpose: Monitor offline queue state and provide sync controls
 *
 * Features:
 *   - Real-time queue length tracking
 *   - Sync status monitoring (isSyncing)
 *   - Manual sync trigger
 *   - Sync progress updates
 *   - Error handling
 *
 * Usage:
 *   const { queueLength, isSyncing, manualSync, syncProgress } = useOfflineQueue();
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { useState, useEffect, useCallback } from 'react';
import { syncService, type SyncProgress } from '@/services/syncService';
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { logger } from '@/services/logger';

interface UseOfflineQueueReturn {
  /** Current number of items in queue */
  queueLength: number;
  /** Whether sync is currently in progress */
  isSyncing: boolean;
  /** Trigger manual sync */
  manualSync: () => Promise<void>;
  /** Current sync progress (if syncing) */
  syncProgress: SyncProgress | null;
  /** Last sync error (if any) */
  lastError: string | null;
  /** Queue statistics */
  queueStats: {
    total: number;
    pending: number;
    processing: number;
    failed: number;
  } | null;
}

/**
 * Hook to monitor and control offline queue
 */
export const useOfflineQueue = (): UseOfflineQueueReturn => {
  const [queueLength, setQueueLength] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [queueStats, setQueueStats] = useState<UseOfflineQueueReturn['queueStats']>(null);

  /**
   * Refresh queue length from IndexedDB
   */
  const refreshQueueLength = useCallback(async () => {
    try {
      const size = await syncService.getQueueSize();
      setQueueLength(size);
      logger.debug(`Queue length: ${size}`, undefined, 'useOfflineQueue');
    } catch (error) {
      logger.error('Failed to refresh queue length', error, 'useOfflineQueue');
    }
  }, []);

  /**
   * Refresh queue statistics
   */
  const refreshQueueStats = useCallback(async () => {
    try {
      const stats = await syncService.getQueueStats();
      setQueueStats(stats);
      logger.debug('Queue stats refreshed', stats, 'useOfflineQueue');
    } catch (error) {
      logger.error('Failed to refresh queue stats', error, 'useOfflineQueue');
    }
  }, []);

  /**
   * Manual sync trigger
   */
  const manualSync = useCallback(async () => {
    try {
      logger.info('Manual sync triggered', undefined, 'useOfflineQueue');
      setLastError(null);

      // Process queue
      const result = await syncService.processQueue();

      logger.info('Manual sync completed', result, 'useOfflineQueue');

      // Refresh queue data
      await refreshQueueLength();
      await refreshQueueStats();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastError(errorMessage);
      logger.error('Manual sync failed', error, 'useOfflineQueue');
    }
  }, [refreshQueueLength, refreshQueueStats]);

  /**
   * Subscribe to sync events
   */
  useEffect(() => {
    // Initial load
    refreshQueueLength();
    refreshQueueStats();

    // Subscribe to sync started event
    const unsubscribeSyncStarted = eventBus.on(EVENT_TYPES.SYNC_STARTED, () => {
      logger.debug('Sync started', undefined, 'useOfflineQueue');
      setIsSyncing(true);
      setSyncProgress(null);
      setLastError(null);
    });

    // Subscribe to sync progress event
    const unsubscribeSyncProgress = eventBus.on(EVENT_TYPES.SYNC_PROGRESS, (payload: unknown) => {
      const progress = payload as SyncProgress;
      logger.debug('Sync progress', progress, 'useOfflineQueue');
      setSyncProgress(progress);

      // Refresh queue length when checkout is queued (offline transaction)
      if ((payload as any)?.action === 'checkout_queued') {
        logger.debug('Checkout queued, refreshing queue length', undefined, 'useOfflineQueue');
        refreshQueueLength();
        refreshQueueStats();
      }
    });

    // Subscribe to sync completed event
    const unsubscribeSyncCompleted = eventBus.on(EVENT_TYPES.SYNC_COMPLETED, (payload: unknown) => {
      const progress = payload as SyncProgress;
      logger.info('Sync completed', progress, 'useOfflineQueue');
      setIsSyncing(false);
      setSyncProgress(progress);

      // Refresh queue after completion
      refreshQueueLength();
      refreshQueueStats();
    });

    // Subscribe to sync failed event
    const unsubscribeSyncFailed = eventBus.on(EVENT_TYPES.SYNC_FAILED, (payload: unknown) => {
      const error = (payload as { error?: unknown; reason?: string }).error;
      const reason = (payload as { error?: unknown; reason?: string }).reason;
      const errorMessage =
        reason || (error instanceof Error ? error.message : String(error)) || 'Unknown error';

      logger.error('Sync failed', payload, 'useOfflineQueue');
      setIsSyncing(false);
      setLastError(errorMessage);

      // Refresh queue after failure
      refreshQueueLength();
      refreshQueueStats();
    });

    // Subscribe to inventory updated event (queue might have changed)
    const unsubscribeInventoryUpdated = eventBus.on(EVENT_TYPES.INVENTORY_UPDATED, () => {
      logger.debug('Inventory updated, refreshing queue', undefined, 'useOfflineQueue');
      refreshQueueLength();
    });

    // Cleanup
    return () => {
      unsubscribeSyncStarted();
      unsubscribeSyncProgress();
      unsubscribeSyncCompleted();
      unsubscribeSyncFailed();
      unsubscribeInventoryUpdated();
    };
  }, [refreshQueueLength, refreshQueueStats]);

  return {
    queueLength,
    isSyncing,
    manualSync,
    syncProgress,
    lastError,
    queueStats,
  };
};
